import { Card } from '@avanta/shared/components/ui/Card';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useFetchPatient } from '@avanta/shared/hooks/useFetchPatient';
import { genderOptions } from '@avanta/shared/types/patients';
import { Building, type LucideIcon, Mail, Phone, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { InfoRow } from 'components/InfoRow';
import { UserProfileSummary } from 'components/UserProfileSummary';
import { useFetchEnterprise } from 'hooks/useFetchEnterprise';

interface PatientInfoProps {
  patientId: string;
}

export const PatientInfo = ({ patientId }: PatientInfoProps): React.ReactElement | null => {
  const { data: patient, isLoading: isLoadingPatient } = useFetchPatient(patientId);
  const { data: enterprise } = useFetchEnterprise({ enterpriseId: patient?.enterpriseId });
  const { t } = useTranslation(LOCIZE_NS.PSYCHOLOGIST);

  if (isLoadingPatient || !patient) {
    return null;
  }

  const genderOpt = genderOptions.find(opt => opt.value === patient.gender);
  const genderText = genderOpt ? t(genderOpt.label, { ns: genderOpt.ns }) : '';

  const info: { data: string; icon: LucideIcon }[] = [
    {
      data: `${patient.age} (${genderText})`,
      icon: User,
    },
    ...(patient.email ? [{ data: patient.email, icon: Mail }] : []),
    ...(patient.contactPhone ? [{ data: patient.contactPhone, icon: Phone }] : []),
    ...(enterprise?.name ? [{ data: enterprise.name, icon: Building }] : []),
  ];

  return (
    <Card className="bg-white">
      <div className="flex flex-col gap-6">
        <UserProfileSummary
          firstName={patient.firstName}
          surname={patient.surname}
          nickname={patient.nickname}
          userId={patient.id}
          enterpriseId={patient.enterpriseId}
        />
        <hr />
        <div className="flex flex-col gap-4">
          <h3 className="text-lg font-medium">
            {t('Chat.UserInfo.personalInfo.heading', { ns: LOCIZE_NS.CHAT })}
          </h3>
          {info.map(({ data, icon }) => (
            <InfoRow key={data} data={data} icon={icon} />
          ))}
        </div>
      </div>
    </Card>
  );
};
